// pages/dashboard/Enterprise.jsx
// Steve manages all enterprise company accounts here
// View companies, add new, see completion rates

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const copyToClipboard = (text, onSuccess, onFail) => {
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(text).then(onSuccess).catch(onFail);
  } else {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try { document.execCommand('copy'); onSuccess(); } catch (err) { onFail(err); }
    document.body.removeChild(textarea);
  }
};

const Enterprise = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [sharedLinks, setSharedLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAddLink, setShowAddLink] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('companies');
  const [form, setForm] = useState({
    companyName: '', contactName: '', contactEmail: '',
    contactPhone: '', industry: '', ratePerAssessment: 149, notes: '',
  });
  const [linkForm, setLinkForm] = useState({ label: '', notes: '', companyId: '', maxUses: '' });

  useEffect(() => {
    fetchCompanies();
    fetchSharedLinks();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/enterprise/companies');
      if (response.data.success) setCompanies(response.data.data.companies);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSharedLinks = async () => {
    try {
      const response = await axios.get('/enterprise/shared-links');
      if (response.data.success) setSharedLinks(response.data.data.links);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.post('/enterprise/companies', form);
      if (response.data.success) {
        setMessage({ type: 'success', text: `Company created with passcode: ${response.data.data.company.passcode}` });
        setShowAddForm(false);
        setForm({ companyName: '', contactName: '', contactEmail: '', contactPhone: '', industry: '', ratePerAssessment: 149, notes: '' });
        fetchCompanies();
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to create company' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateLink = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await axios.post('/enterprise/shared-links', linkForm);
      if (response.data.success) {
        setMessage({ type: 'success', text: `Link created: ${response.data.data.link.url}` });
        setShowAddLink(false);
        setLinkForm({ label: '', notes: '', companyId: '', maxUses: '' });
        fetchSharedLinks();
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to create link' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleLink = async (id, active) => {
    try {
      await axios.put(`/enterprise/shared-links/${id}`, { active });
      fetchSharedLinks();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update link' });
    }
  };

  const handleDeleteLink = async (id) => {
    if (!window.confirm('Delete this link? Anyone with this link will no longer be able to access the assessment.')) return;
    try {
      await axios.delete(`/enterprise/shared-links/${id}`);
      fetchSharedLinks();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete link' });
    }
  };

  const handleCopyLink = (text) => {
    copyToClipboard(
      text,
      () => {
        setMessage({ type: 'success', text: 'Link copied to clipboard!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 2000);
      },
      () => setMessage({ type: 'error', text: 'Could not copy. Please copy manually.' })
    );
  };

  const handleLogout = async () => { await logout(); navigate('/admin/login'); };

  const navItems = [
    { label: 'Dashboard', path: '/admin/dashboard', icon: '📊' },
    { label: 'Submissions', path: '/admin/submissions', icon: '📋' },
    { label: 'Transactions', path: '/admin/transactions', icon: '💳' },
    { label: 'Meetings', path: '/admin/meetings', icon: '📅' },
    { label: 'Enterprise', path: '/admin/enterprise', icon: '🏢', active: true },
    { label: 'Form Maker', path: '/admin/forms', icon: '📝' },
    { label: 'Analytics', path: '/admin/analytics', icon: '📈' },
    { label: 'Settings', path: '/admin/settings', icon: '⚙️' },
  ];

  return (
    <div style={s.page}>
      <div style={s.sidebar}>
        <div style={s.sidebarLogo}>
          <span style={s.logoExec}>EXECUTIVE</span>
          <span style={s.logoSpring}>Springboard</span>
        </div>
        <nav style={s.nav}>
          {navItems.map(item => (
            <button key={item.path} style={{ ...s.navItem, ...(item.active ? s.navActive : {}) }} onClick={() => navigate(item.path)}>
              <span>{item.icon}</span><span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div style={s.sidebarFooter}>
          <div style={s.adminName}>{admin?.name}</div>
          <button style={s.logoutBtn} onClick={handleLogout}>Sign Out</button>
        </div>
      </div>

      <div style={s.main}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Enterprise</h1>
            <p style={s.subtitle}>{companies.length} companies enrolled</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {activeTab === 'companies' && (
              <button style={s.btnPrimary} onClick={() => setShowAddForm(!showAddForm)}>
                {showAddForm ? 'Cancel' : '+ Add Company'}
              </button>
            )}
            {activeTab === 'links' && (
              <button style={s.btnPrimary} onClick={() => setShowAddLink(!showAddLink)}>
                {showAddLink ? 'Cancel' : '+ Create Shared Link'}
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', backgroundColor: '#fff', padding: '4px', borderRadius: '10px', border: '1px solid var(--color-border)', width: 'fit-content', marginBottom: '20px' }}>
          <button
            style={{ padding: '9px 20px', borderRadius: '7px', border: 'none', backgroundColor: activeTab === 'companies' ? 'var(--color-primary)' : 'transparent', color: activeTab === 'companies' ? '#fff' : 'var(--color-text-light)', fontSize: '14px', cursor: 'pointer', fontWeight: '500' }}
            onClick={() => setActiveTab('companies')}
          >
            🏢 Companies ({companies.length})
          </button>
          <button
            style={{ padding: '9px 20px', borderRadius: '7px', border: 'none', backgroundColor: activeTab === 'links' ? 'var(--color-primary)' : 'transparent', color: activeTab === 'links' ? '#fff' : 'var(--color-text-light)', fontSize: '14px', cursor: 'pointer', fontWeight: '500' }}
            onClick={() => setActiveTab('links')}
          >
            🔗 Shared Links ({sharedLinks.length})
          </button>
        </div>

        {message.text && (
          <div style={{ ...s.messageBox, ...(message.type === 'success' ? s.messageSuccess : s.messageError) }}>
            {message.text}
          </div>
        )}

        {/* COMPANIES TAB */}
        {activeTab === 'companies' && (
          <>
            {showAddForm && (
              <div style={s.addForm}>
                <h2 style={s.addFormTitle}>Add New Company</h2>
                <p style={s.addFormSubtitle}>A unique passcode will be automatically generated for this company.</p>
                <form onSubmit={handleAddCompany}>
                  <div style={s.formRow}>
                    <div style={s.formGroup}>
                      <label style={s.label}>Company Name *</label>
                      <input style={s.input} type="text" placeholder="Acme Corporation" value={form.companyName} onChange={e => setForm(p => ({ ...p, companyName: e.target.value }))} required />
                    </div>
                    <div style={s.formGroup}>
                      <label style={s.label}>Industry</label>
                      <input style={s.input} type="text" placeholder="Technology" value={form.industry} onChange={e => setForm(p => ({ ...p, industry: e.target.value }))} />
                    </div>
                  </div>
                  <div style={s.formRow}>
                    <div style={s.formGroup}>
                      <label style={s.label}>Contact Name *</label>
                      <input style={s.input} type="text" placeholder="John Smith" value={form.contactName} onChange={e => setForm(p => ({ ...p, contactName: e.target.value }))} required />
                    </div>
                    <div style={s.formGroup}>
                      <label style={s.label}>Contact Email *</label>
                      <input style={s.input} type="email" placeholder="john@company.com" value={form.contactEmail} onChange={e => setForm(p => ({ ...p, contactEmail: e.target.value }))} required />
                    </div>
                  </div>
                  <div style={s.formRow}>
                    <div style={s.formGroup}>
                      <label style={s.label}>Contact Phone</label>
                      <input style={s.input} type="text" placeholder="+1 555 0000" value={form.contactPhone} onChange={e => setForm(p => ({ ...p, contactPhone: e.target.value }))} />
                    </div>
                    <div style={s.formGroup}>
                      <label style={s.label}>Rate per Assessment ($)</label>
                      <input style={s.input} type="number" value={form.ratePerAssessment} onChange={e => setForm(p => ({ ...p, ratePerAssessment: Number(e.target.value) }))} />
                    </div>
                  </div>
                  <div style={s.formGroup}>
                    <label style={s.label}>Notes</label>
                    <textarea style={s.textarea} rows={2} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
                  </div>
                  <button type="submit" style={s.btnSubmit} disabled={submitting}>
                    {submitting ? 'Creating...' : 'Create Company'}
                  </button>
                </form>
              </div>
            )}

            {loading ? (
              <p style={s.loadingText}>Loading companies...</p>
            ) : companies.length === 0 ? (
              <div style={s.empty}>
                <div style={s.emptyIcon}>🏢</div>
                <p style={s.emptyTitle}>No enterprise companies yet</p>
                <p style={s.emptyText}>Add your first company to get started with enterprise assessments.</p>
              </div>
            ) : (
              <div style={s.companiesGrid}>
                {companies.map(company => (
                  <div key={company._id} style={s.companyCard}>
                    <div style={s.companyHeader}>
                      <div>
                        <h3 style={s.companyName}>{company.companyName}</h3>
                        <p style={s.companyIndustry}>{company.industry || 'No industry set'}</p>
                      </div>
                      <span style={{ ...s.statusBadge, ...(company.status === 'active' ? s.statusActive : s.statusInactive) }}>
                        {company.status}
                      </span>
                    </div>
                    <div style={s.passcodeBox}>
                      <span style={s.passcodeLabel}>Company Code:</span>
                      <span style={s.passcodeValue}>{company.passcode}</span>
                    </div>
                    <div style={s.statsRow}>
                      <div style={s.statItem}>
                        <div style={s.statValue}>{company.memberCount || 0}</div>
                        <div style={s.statLabel}>Members</div>
                      </div>
                      <div style={s.statItem}>
                        <div style={s.statValue}>{company.completedCount || 0}</div>
                        <div style={s.statLabel}>Completed</div>
                      </div>
                      <div style={s.statItem}>
                        <div style={{ ...s.statValue, color: 'var(--color-teal)' }}>{company.completionRate || 0}%</div>
                        <div style={s.statLabel}>Rate</div>
                      </div>
                      <div style={s.statItem}>
                        <div style={s.statValue}>${company.ratePerAssessment}</div>
                        <div style={s.statLabel}>Per Assessment</div>
                      </div>
                    </div>
                    <div style={s.contactRow}>
                      <span style={s.contactName}>{company.contactName}</span>
                      <span style={s.contactEmail}>{company.contactEmail}</span>
                    </div>
                    <div style={s.cardActions}>
                      <button style={s.btnManage} onClick={() => navigate(`/admin/enterprise/${company._id}`)}>
                        Manage Members →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* SHARED LINKS TAB */}
        {activeTab === 'links' && (
          <>
            <div style={{ backgroundColor: '#EBF5FB', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px', fontSize: '13px', color: '#1A5276', lineHeight: '1.6' }}>
              ℹ️ Shared links give anyone access to the assessment without paying. Share these links with clients, mentees, or anyone Steve wants to give complimentary access to.
            </div>

            {showAddLink && (
              <div style={s.addForm}>
                <h2 style={s.addFormTitle}>Create Shared Link</h2>
                <form onSubmit={handleCreateLink}>
                  <div style={s.formGroup}>
                    <label style={s.label}>Label * (for your reference)</label>
                    <input style={s.input} type="text" placeholder="e.g. Client Carrie Sukup, Mentee Cohort June 2026" value={linkForm.label} onChange={e => setLinkForm(p => ({ ...p, label: e.target.value }))} required autoFocus />
                  </div>
                  <div style={s.formGroup}>
                    <label style={s.label}>Link to Company (optional)</label>
                    <select style={s.input} value={linkForm.companyId} onChange={e => setLinkForm(p => ({ ...p, companyId: e.target.value }))}>
                      <option value="">No company (individual access)</option>
                      {companies.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                    </select>
                  </div>
                  <div style={s.formGroup}>
                    <label style={s.label}>Notes (optional)</label>
                    <input style={s.input} type="text" placeholder="Any notes about this link..." value={linkForm.notes} onChange={e => setLinkForm(p => ({ ...p, notes: e.target.value }))} />
                  </div>
                  <div style={s.formGroup}>
                    <label style={s.label}>Max uses (optional)</label>
                    <input style={s.input} type="number" min="1" placeholder="Leave empty for unlimited (∞)" value={linkForm.maxUses} onChange={e => setLinkForm(p => ({ ...p, maxUses: e.target.value }))} />
                    <div style={{ fontSize: '12px', color: 'var(--color-text-light)', marginTop: '4px' }}>
                      How many completed assessments this link allows. Empty = unlimited ∞. Set 1 for a single person.
                    </div>
                  </div>
                  <button type="submit" style={s.btnSubmit} disabled={submitting}>
                    {submitting ? 'Creating...' : 'Create Link'}
                  </button>
                </form>
              </div>
            )}

            {sharedLinks.length === 0 ? (
              <div style={s.empty}>
                <div style={s.emptyIcon}>🔗</div>
                <p style={s.emptyTitle}>No shared links yet</p>
                <p style={s.emptyText}>Create a shared link to give someone free access to the assessment without needing a company code or payment.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {sharedLinks.map(link => (
                  <div key={link._id} style={{ backgroundColor: '#fff', borderRadius: '12px', border: `1px solid ${link.active ? 'var(--color-border)' : '#E0E0E0'}`, padding: '20px', opacity: link.active ? 1 : 0.6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                      <div>
                        <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '4px' }}>
                          {link.label}
                          <span style={{ marginLeft: '10px', fontSize: '11px', fontWeight: '500', padding: '2px 8px', borderRadius: '100px', backgroundColor: link.active ? '#E8F8EF' : '#F4F6F9', color: link.active ? '#1E8449' : '#7F8C8D' }}>
                            {link.active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        {link.companyId && <div style={{ fontSize: '12px', color: 'var(--color-text-light)', marginBottom: '4px' }}>🏢 {link.companyId.companyName}</div>}
                        {link.notes && <div style={{ fontSize: '12px', color: 'var(--color-text-light)', fontStyle: 'italic', marginBottom: '8px' }}>{link.notes}</div>}
                        <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: 'var(--color-text-light)' }}>
                          <span>👁 {link.totalClicks} clicks</span>
                          <span>📋 {link.totalSubmissions} / {link.maxUses != null ? link.maxUses : '∞'} uses</span>
                          {link.maxUses != null && link.totalSubmissions >= link.maxUses && (
                            <span style={{ color: '#943126', fontWeight: '600' }}>⛔ Limit reached</span>
                          )}
                          <span>📅 Created {new Date(link.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          style={{ backgroundColor: 'var(--color-teal)', color: '#fff', padding: '8px 14px', borderRadius: '6px', border: 'none', fontSize: '12px', cursor: 'pointer' }}
                          onClick={() => handleCopyLink(link.url)}
                        >
                          📋 Copy Link
                        </button>
                        <button
                          style={{ backgroundColor: 'var(--color-off-white)', color: 'var(--color-text)', padding: '8px 14px', borderRadius: '6px', border: '1px solid var(--color-border)', fontSize: '12px', cursor: 'pointer' }}
                          onClick={() => handleToggleLink(link._id, !link.active)}
                        >
                          {link.active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          style={{ backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error)', padding: '8px 14px', borderRadius: '6px', border: '1px solid var(--color-error)', fontSize: '12px', cursor: 'pointer' }}
                          onClick={() => handleDeleteLink(link._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <div style={{ marginTop: '12px', backgroundColor: 'var(--color-off-white)', borderRadius: '6px', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '11px', color: 'var(--color-text-light)', flex: 1, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {link.url}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const s = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: 'var(--font-primary)' },
  sidebar: { width: '240px', backgroundColor: 'var(--color-primary)', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  sidebarLogo: { display: 'flex', flexDirection: 'column', gap: '2px', padding: '24px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' },
  logoExec: { fontSize: '9px', fontWeight: '700', letterSpacing: '3px', color: 'var(--color-teal)', lineHeight: 1 },
  logoSpring: { fontSize: '18px', fontWeight: '700', color: '#fff', lineHeight: 1 },
  nav: { flex: 1, padding: '16px 0' },
  navItem: { width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '14px', cursor: 'pointer', textAlign: 'left' },
  navActive: { backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' },
  sidebarFooter: { padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' },
  adminName: { fontSize: '13px', fontWeight: '600', color: '#fff', marginBottom: '10px' },
  logoutBtn: { width: '100%', padding: '8px', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'rgba(255,255,255,0.5)', fontSize: '12px', cursor: 'pointer' },
  main: { flex: 1, backgroundColor: 'var(--color-dashboard-bg)', padding: '32px', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '26px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '4px' },
  subtitle: { fontSize: '14px', color: 'var(--color-text-light)' },
  btnPrimary: { backgroundColor: 'var(--color-teal)', color: '#fff', padding: '10px 20px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  messageBox: { padding: '12px 16px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px' },
  messageSuccess: { backgroundColor: 'var(--color-success-bg)', color: 'var(--color-success)', border: '1px solid #A9DFBF' },
  messageError: { backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error)', border: '1px solid #F5C6CB' },
  addForm: { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', padding: '28px', marginBottom: '24px', boxShadow: 'var(--shadow-sm)' },
  addFormTitle: { fontSize: '18px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '4px' },
  addFormSubtitle: { fontSize: '13px', color: 'var(--color-text-light)', marginBottom: '20px' },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--color-text)', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', border: '1.5px solid var(--color-border)', borderRadius: '8px', fontSize: '14px', color: 'var(--color-text)', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 14px', border: '1.5px solid var(--color-border)', borderRadius: '8px', fontSize: '14px', color: 'var(--color-text)', outline: 'none', boxSizing: 'border-box', resize: 'vertical' },
  btnSubmit: { backgroundColor: 'var(--color-teal)', color: '#fff', padding: '12px 28px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  loadingText: { padding: '40px', textAlign: 'center', color: 'var(--color-text-light)' },
  empty: { padding: '60px 20px', textAlign: 'center', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)' },
  emptyIcon: { fontSize: '48px', marginBottom: '16px' },
  emptyTitle: { fontSize: '18px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '8px' },
  emptyText: { fontSize: '14px', color: 'var(--color-text-light)' },
  companiesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' },
  companyCard: { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', padding: '20px', boxShadow: 'var(--shadow-sm)' },
  companyHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' },
  companyName: { fontSize: '16px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '2px' },
  companyIndustry: { fontSize: '12px', color: 'var(--color-text-light)' },
  statusBadge: { padding: '3px 10px', borderRadius: '100px', fontSize: '11px', fontWeight: '500' },
  statusActive: { backgroundColor: '#E8F8EF', color: '#1E8449' },
  statusInactive: { backgroundColor: '#F4F6F9', color: '#7F8C8D' },
  passcodeBox: { backgroundColor: 'var(--color-off-white)', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '10px' },
  passcodeLabel: { fontSize: '12px', color: 'var(--color-text-light)', fontWeight: '500' },
  passcodeValue: { fontSize: '16px', fontWeight: '800', color: 'var(--color-teal)', letterSpacing: '2px', fontFamily: 'monospace' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '14px' },
  statItem: { textAlign: 'center' },
  statValue: { fontSize: '18px', fontWeight: '700', color: 'var(--color-primary)' },
  statLabel: { fontSize: '10px', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  contactRow: { display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '14px', paddingTop: '12px', borderTop: '1px solid var(--color-border)' },
  contactName: { fontSize: '13px', fontWeight: '500', color: 'var(--color-text)' },
  contactEmail: { fontSize: '12px', color: 'var(--color-text-light)' },
  cardActions: { display: 'flex', justifyContent: 'flex-end' },
  btnManage: { backgroundColor: 'var(--color-teal)', color: '#fff', padding: '8px 16px', borderRadius: '6px', border: 'none', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
};

export default Enterprise;
